import { boxesIntersectOBB, type Box2D } from './collisionGuard.logic.ts'

export type StructuralGeometryKind = 'wall' | 'door' | 'column' | 'restricted_zone' | 'boundary'

export type StructuralGeometry = {
  id: string
  kind: StructuralGeometryKind
  xM: number
  yM: number
  widthM: number
  depthM: number
  rotation?: number
  blocksPlacement?: boolean
  blocksRouting?: boolean
  x_m?: number
  y_m?: number
  width_m?: number
  depth_m?: number
  blocks_placement?: boolean
  blocks_routing?: boolean
}

export type PlacementConflict = {
  id: string
  kind: StructuralGeometryKind | 'bounds'
  reason: 'structural_collision' | 'out_of_bounds'
}

export function structuralGeometryToBoxes(structures: StructuralGeometry[], ppm = 100): Box2D[] {
  return structures
    .filter(structure => (structure.blocksPlacement ?? structure.blocks_placement) !== false)
    .map(structure => ({
      id: structure.id,
      x: (structure.xM ?? structure.x_m ?? 0) * ppm,
      y: (structure.yM ?? structure.y_m ?? 0) * ppm,
      w: (structure.widthM ?? structure.width_m ?? 0) * ppm,
      h: (structure.depthM ?? structure.depth_m ?? 0) * ppm,
      rotation: structure.rotation ?? 0,
    }))
}

function rotatedBounds(box: Box2D) {
  const cx = box.x + box.w / 2
  const cy = box.y + box.h / 2
  const rad = (box.rotation ?? 0) * Math.PI / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)
  const points = [[-box.w / 2, -box.h / 2], [box.w / 2, -box.h / 2], [box.w / 2, box.h / 2], [-box.w / 2, box.h / 2]]
    .map(([x, y]) => ({ x: cx + x * cos - y * sin, y: cy + x * sin + y * cos }))
  return {
    minX: Math.min(...points.map(point => point.x)),
    minY: Math.min(...points.map(point => point.y)),
    maxX: Math.max(...points.map(point => point.x)),
    maxY: Math.max(...points.map(point => point.y)),
  }
}

export function validateStructuralPlacement(
  candidate: Box2D,
  structures: StructuralGeometry[],
  bounds?: { widthM: number; heightM: number; originXM?: number; originYM?: number; ppm?: number },
): { valid: boolean; conflicts: PlacementConflict[] } {
  const conflicts: PlacementConflict[] = []
  for (const structure of structures) {
    if ((structure.blocksPlacement ?? structure.blocks_placement) === false) continue
    const obstacle = structuralGeometryToBoxes([structure], bounds?.ppm ?? 100)[0]
    if (boxesIntersectOBB(candidate, obstacle)) {
      conflicts.push({ id: structure.id, kind: structure.kind, reason: 'structural_collision' })
    }
  }

  if (bounds) {
    const ppm = bounds.ppm ?? 100
    const candidateBounds = rotatedBounds(candidate)
    const minX = (bounds.originXM ?? 0) * ppm
    const minY = (bounds.originYM ?? 0) * ppm
    const maxX = minX + bounds.widthM * ppm
    const maxY = minY + bounds.heightM * ppm
    if (candidateBounds.minX < minX || candidateBounds.minY < minY || candidateBounds.maxX > maxX || candidateBounds.maxY > maxY) {
      conflicts.push({ id: 'floor-plan-bounds', kind: 'bounds', reason: 'out_of_bounds' })
    }
  }
  return { valid: conflicts.length === 0, conflicts }
}
